import { useEffect, useState, type ReactNode } from 'react';
import { Delete, LogIn } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkPinPadProps {
  onSubmit: (pin: string) => void;
  busy?: boolean;
  error?: string | null;
  minLength?: number;
  maxLength?: number;
  className?: string;
  /** Resets the entered digits when this value changes (e.g. after an error). */
  resetKey?: number;
}

/**
 * Big-key numeric pad for the station tablet. Also accepts the physical
 * keyboard (digits / Backspace / Enter) so a keyboard-equipped kiosk works.
 */
export function WorkPinPad({
  onSubmit,
  busy = false,
  error,
  minLength = 4,
  maxLength = 8,
  className,
  resetKey = 0,
}: WorkPinPadProps) {
  const [pin, setPin] = useState('');

  useEffect(() => {
    setPin('');
  }, [resetKey]);

  const push = (d: string) => {
    if (busy) return;
    setPin((p) => (p.length >= maxLength ? p : p + d));
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const submit = () => {
    if (busy || pin.length < minLength) return;
    onSubmit(pin);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        push(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        back();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, busy, minLength, maxLength]);

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <div className={cn('flex w-full max-w-xs flex-col items-center gap-5', className)}>
      <div className="flex h-8 items-center gap-3" aria-label={`${pin.length} digits entered`}>
        {Array.from({ length: Math.max(minLength, pin.length) }).map((_, i) => (
          <span
            key={i}
            className={cn(
              'h-3.5 w-3.5 rounded-full border-2 transition-colors',
              i < pin.length ? 'border-primary bg-primary' : 'border-border bg-transparent',
            )}
          />
        ))}
      </div>
      <p className={cn('min-h-5 text-center text-sm', error ? 'text-destructive' : 'text-muted-foreground')} aria-live="polite">
        {error ?? `Enter your ${minLength}–${maxLength} digit PIN`}
      </p>
      <div className="grid w-full grid-cols-3 gap-3">
        {keys.map((k) => (
          <PadKey key={k} onClick={() => push(k)} disabled={busy} label={k} />
        ))}
        <PadKey onClick={back} disabled={busy || pin.length === 0} label={<Delete className="h-6 w-6" />} aria="Delete" subtle />
        <PadKey onClick={() => push('0')} disabled={busy} label="0" />
        <PadKey
          onClick={submit}
          disabled={busy || pin.length < minLength}
          label={<LogIn className="h-6 w-6" />}
          aria="Sign in"
          primary
        />
      </div>
    </div>
  );
}

function PadKey({
  label,
  onClick,
  disabled,
  aria,
  subtle,
  primary,
}: {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  aria?: string;
  subtle?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={aria}
      className={cn(
        'flex h-16 items-center justify-center rounded-2xl border text-2xl font-medium transition-colors select-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100',
        primary
          ? 'border-transparent bg-primary text-primary-foreground hover:bg-old-growth'
          : subtle
            ? 'border-transparent bg-transparent text-muted-foreground hover:bg-secondary'
            : 'border-border bg-card text-foreground hover:bg-secondary',
      )}
    >
      {label}
    </button>
  );
}
